import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { api_v3_leaderboard } from "../../utils/constants";

const HomeMiniLeaderboard = () => {
  const [entries, setEntries] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(api_v3_leaderboard());
        if (!cancelled) {
          const data = res.data || {};
          const items = data.items || [];
          setAllItems(items);
          setEntries(items.slice(0, 10));
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const onFire = React.useMemo(() => {
    const valid = allItems.filter(e => e.eloChange24h != null && e.eloChange24h > 0);
    if (valid.length === 0) return null;
    return valid.reduce((a, b) => (a.eloChange24h > b.eloChange24h ? a : b));
  }, [allItems]);

  const titanic = React.useMemo(() => {
    const valid = allItems.filter(e => e.eloChange24h != null && e.eloChange24h < 0);
    if (valid.length === 0) return null;
    return valid.reduce((a, b) => (a.eloChange24h < b.eloChange24h ? a : b));
  }, [allItems]);

  const getInitialAvatar = (name) => {
    const initial = (name || "?")[0].toUpperCase();
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28">
      <rect width="28" height="28" rx="14" fill="#3a3a4a"/>
      <text x="14" y="14" text-anchor="middle" dominant-baseline="central" fill="rgba(255,255,255,0.6)" font-family="sans-serif" font-size="14" font-weight="600">${initial}</text>
    </svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  };

  return (
    <Wrapper className="v-anim-fade-up v-delay-4">
      <HeaderRow>
        <SectionLabel>CLASIFICACIÓN</SectionLabel>
        <ViewAllLink to="/leaderboard">VER TODO →</ViewAllLink>
      </HeaderRow>
      <SectionTitle>TOP 10</SectionTitle>

      {loading && (
        <StateText>Cargando...</StateText>
      )}

      {error && (
        <StateText>Error al cargar</StateText>
      )}

      {!loading && !error && entries.length === 0 && (
        <StateText>Sin datos</StateText>
      )}

      {!loading && !error && entries.length > 0 && (
        <List>
          {entries.map((entry, i) => (
            <Row key={entry.playerId}>
              <Rank>{i + 1}</Rank>
              <PlayerInfo>
                <Avatar
                  src={
                    entry.discordAvatarURL ||
                    "https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/default-avatar.png"
                  }
                  alt={entry.username}
                  onError={(e) => {
                    if (!e.target.dataset.fallback) {
                      e.target.dataset.fallback = "1";
                      e.target.src = getInitialAvatar(entry.username);
                    }
                  }}
                />
                <NameLink to={`/player/${entry.playerId}`}>
                  {entry.username}
                </NameLink>
              </PlayerInfo>
              <Elo>{entry.avgElo ?? "—"}</Elo>
            </Row>
          ))}
        </List>
      )}

      {!loading && !error && allItems.length > 0 && (
        <StatsRow>
          <StatCard>
            <StatLabel>
              <FireIcon>▲</FireIcon> ON FIRE
            </StatLabel>
            {onFire ? (
              <StatPlayer>
                <StatAvatar
                  src={
                    onFire.discordAvatarURL ||
                    "https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/default-avatar.png"
                  }
                  alt={onFire.username}
                  onError={(e) => {
                    if (!e.target.dataset.fallback) {
                      e.target.dataset.fallback = "1";
                      e.target.src = getInitialAvatar(onFire.username);
                    }
                  }}
                />
                <StatName to={`/player/${onFire.playerId}`}>
                  {onFire.username}
                </StatName>
                <StatChange $positive>+{onFire.eloChange24h}</StatChange>
              </StatPlayer>
            ) : (
              <StatEmpty>—</StatEmpty>
            )}
          </StatCard>
          <StatCard>
            <StatLabel>
              <IceIcon>▼</IceIcon> TITANIC
            </StatLabel>
            {titanic ? (
              <StatPlayer>
                <StatAvatar
                  src={
                    titanic.discordAvatarURL ||
                    "https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/default-avatar.png"
                  }
                  alt={titanic.username}
                  onError={(e) => {
                    if (!e.target.dataset.fallback) {
                      e.target.dataset.fallback = "1";
                      e.target.src = getInitialAvatar(titanic.username);
                    }
                  }}
                />
                <StatName to={`/player/${titanic.playerId}`}>
                  {titanic.username}
                </StatName>
                <StatChange $negative>{titanic.eloChange24h}</StatChange>
              </StatPlayer>
            ) : (
              <StatEmpty>—</StatEmpty>
            )}
          </StatCard>
        </StatsRow>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
  opacity: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

const SectionLabel = styled.p`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.8px;
  color: var(--v-mint);
  margin: 0;
`;

const ViewAllLink = styled(Link)`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-secondary);
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: var(--v-mint);
  }
`;

const SectionTitle = styled.h2`
  font-family: var(--v-font-ui);
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  color: var(--v-white);
  margin: 0 0 1rem 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.5rem;
  border-radius: 10px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const Rank = styled.span`
  font-family: var(--v-font-mono);
  font-size: 12px;
  font-weight: 700;
  color: var(--v-secondary);
  min-width: 22px;
  text-align: center;
  flex-shrink: 0;
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;

const Avatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const NameLink = styled(Link)`
  font-family: var(--v-font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--v-white);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.15s ease;

  &:hover {
    color: var(--v-mint);
  }
`;

const Elo = styled.span`
  font-family: var(--v-font-mono);
  font-size: 13px;
  font-weight: 700;
  color: var(--v-mint);
  flex-shrink: 0;
`;

const StateText = styled.p`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-secondary);
  text-align: center;
  padding: 2rem 0;
  margin: auto;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const StatCard = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 0.6rem 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const StatLabel = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--v-secondary);
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const FireIcon = styled.span`
  font-size: 10px;
  color: #ff6b35;
`;

const IceIcon = styled.span`
  font-size: 10px;
  color: #4fc3f7;
`;

const StatPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
`;

const StatAvatar = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const StatName = styled(Link)`
  font-family: var(--v-font-ui);
  font-size: 11px;
  font-weight: 600;
  color: var(--v-white);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  transition: color 0.15s ease;

  &:hover {
    color: var(--v-mint);
  }
`;

const StatChange = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  color: ${({ $positive, $negative }) =>
    $positive ? "var(--v-mint)" : $negative ? "#ff4d6a" : "var(--v-secondary)"};
`;

const StatEmpty = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  color: var(--v-secondary);
`;

export default HomeMiniLeaderboard;
